import * as PIXI from 'pixi.js';

/**
 * IsoGridOverlay
 *
 * Renders diamond-shaped isometric grid lines.
 * Isometric projection uses 2:1 ratio (tile width = 2 * tile height).
 *
 * Grid is drawn in isometric screen space where:
 *   screenX = (gridX - gridY) * tileHalfW
 *   screenY = (gridX + gridY) * tileHalfH
 */

const GRID_COLOR = 0x3a342a;
const GRID_ALPHA = 0.35;

export class IsoGridOverlay {
  public container: PIXI.Container;

  private graphics: PIXI.Graphics;
  private gridWidth: number;
  private gridHeight: number;
  private tileWidth: number;
  private tileHeight: number;

  constructor(gridWidth: number, gridHeight: number, tileWidth: number, tileHeight: number) {
    this.container = new PIXI.Container();
    this.graphics = new PIXI.Graphics();
    this.container.addChild(this.graphics);

    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;

    this.draw();
  }

  /** Convert grid coords to isometric screen coords */
  gridToScreen(gx: number, gy: number): { x: number; y: number } {
    const halfW = this.tileWidth / 2;
    const halfH = this.tileHeight / 2;
    return {
      x: (gx - gy) * halfW,
      y: (gx + gy) * halfH,
    };
  }

  /** Convert isometric screen coords back to grid coords */
  screenToGrid(sx: number, sy: number): { gx: number; gy: number } {
    const halfW = this.tileWidth / 2;
    const halfH = this.tileHeight / 2;
    return {
      gx: (sx / halfW + sy / halfH) / 2,
      gy: (sy / halfH - sx / halfW) / 2,
    };
  }

  /** Snap screen coords to nearest grid cell center */
  snapToGrid(sx: number, sy: number): { x: number; y: number } {
    const { gx, gy } = this.screenToGrid(sx, sy);
    const snappedGx = Math.round(gx);
    const snappedGy = Math.round(gy);
    return this.gridToScreen(snappedGx, snappedGy);
  }

  private draw(): void {
    const g = this.graphics;
    g.clear();
    g.lineStyle(1, GRID_COLOR, GRID_ALPHA);

    const w = this.gridWidth;
    const h = this.gridHeight;

    // Draw lines running from top-right to bottom-left (constant gx)
    for (let gx = 0; gx <= w; gx++) {
      const start = this.gridToScreen(gx, 0);
      const end = this.gridToScreen(gx, h);
      g.moveTo(start.x, start.y);
      g.lineTo(end.x, end.y);
    }

    // Draw lines running from top-left to bottom-right (constant gy)
    for (let gy = 0; gy <= h; gy++) {
      const start = this.gridToScreen(0, gy);
      const end = this.gridToScreen(w, gy);
      g.moveTo(start.x, start.y);
      g.lineTo(end.x, end.y);
    }
  }

  /** Update grid dimensions and redraw */
  resize(gridWidth: number, gridHeight: number, tileWidth: number, tileHeight: number): void {
    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.draw();
  }

  destroy(): void {
    this.graphics.destroy();
    this.container.destroy({ children: true });
  }
}
